import { PayloadAction } from '@reduxjs/toolkit'
import {
  AvatarVoiceTab,
  IAiAvatarState,
  IAvatarData,
  IAvatarGenerationParams,
  IAvatarGiftAccess,
  IClonedVoice,
  IPublicAvatar,
  TVoiceCloneUploadStatus
} from 'store/types/aiAvatar'
import { IAvatarModels } from 'types/avatar'
import {
  avatarGenerationInitialParams,
  voiceCloneInitial
} from './initialState'

const reducers = {
  setIsLoadingTabData: (
    state: IAiAvatarState,
    action: PayloadAction<boolean>
  ) => {
    state.isLoadingTabData = action.payload
  },
  setAvatar: (
    state: IAiAvatarState,
    action: PayloadAction<IAvatarData | null>
  ) => {
    state.generationParams.avatar = action.payload
  },
  updateAvatar: (
    state: IAiAvatarState,
    action: PayloadAction<Partial<IAvatarData>>
  ) => {
    if (!state.generationParams.avatar) return
    state.generationParams.avatar = {
      ...state.generationParams.avatar,
      ...action.payload
    }
  },
  setAvatarLoading: (state: IAiAvatarState, action: PayloadAction<boolean>) => {
    state.isAvatarLoading = action.payload
  },
  clearAvatar: (state: IAiAvatarState) => {
    state.generationParams.avatar = null
    // The aspect-ratio switcher lives only in the public-avatars panel, so
    // removing the avatar drops its override too — the next avatar (upload /
    // media library / another public one) starts from the project ratio.
    state.generationParams.aspectRatio = null
    state.isAvatarLoading = false
  },
  setVoiceLangCode: (state: IAiAvatarState, action: PayloadAction<string>) => {
    state.generationParams.voice.langCode = action.payload
  },
  setVoiceSpeakerId: (state: IAiAvatarState, action: PayloadAction<number>) => {
    state.generationParams.voice.speakerId = action.payload
  },
  setVoiceSpeed: (state: IAiAvatarState, action: PayloadAction<number>) => {
    state.generationParams.voice.speed = action.payload
  },
  setVoiceCloneTab: (
    state: IAiAvatarState,
    action: PayloadAction<AvatarVoiceTab>
  ) => {
    state.voiceClone.activeTab = action.payload
  },
  setClonedVoice: (
    state: IAiAvatarState,
    action: PayloadAction<IClonedVoice>
  ) => {
    state.voiceClone.voice = action.payload
    state.voiceClone.uploadStatus = 'idle'
  },
  setVoiceCloneUploadStatus: (
    state: IAiAvatarState,
    action: PayloadAction<TVoiceCloneUploadStatus>
  ) => {
    state.voiceClone.uploadStatus = action.payload
  },
  // Remembers the clone a generation was kicked off with, keyed by the
  // generation's `aiAvatarProjectId`, so the regen form / history prefill can
  // re-attach it after the `setGenerationParams` voice-clone reset (see
  // `restoreClonedVoice`). Never cleared within the session — entries are tiny
  // and a clip stays regenerable with its clone for the session's lifetime.
  rememberGenerationClone: (
    state: IAiAvatarState,
    action: PayloadAction<{ avatarProjectId: number; voice: IClonedVoice }>
  ) => {
    state.generationClones[action.payload.avatarProjectId] =
      action.payload.voice
  },
  // Detaches the cloned voice from the current avatar only — the uploaded file
  // itself stays in My Files.
  clearClonedVoice: (state: IAiAvatarState) => {
    state.voiceClone.voice = null
    state.voiceClone.uploadStatus = 'idle'
  },
  setQuality: (state: IAiAvatarState, action: PayloadAction<number>) => {
    state.generationParams.quality = action.payload
  },
  setSpeechText: (state: IAiAvatarState, action: PayloadAction<string>) => {
    state.generationParams.speechText = action.payload
  },
  setAvatarAspectRatio: (
    state: IAiAvatarState,
    action: PayloadAction<string | null>
  ) => {
    state.generationParams.aspectRatio = action.payload
  },
  setPublicAvatars: (
    state: IAiAvatarState,
    action: PayloadAction<IPublicAvatar[]>
  ) => {
    state.publicAvatars.data = action.payload
  },
  setPublicAvatarsLoading: (
    state: IAiAvatarState,
    action: PayloadAction<boolean>
  ) => {
    state.publicAvatars.isLoading = action.payload
  },
  setAvatarModels: (
    state: IAiAvatarState,
    action: PayloadAction<IAvatarModels[]>
  ) => {
    state.avatarModels.data = action.payload
  },
  setAvatarModelsLoading: (
    state: IAiAvatarState,
    action: PayloadAction<boolean>
  ) => {
    state.avatarModels.isLoading = action.payload
  },
  setGiftAccess: (
    state: IAiAvatarState,
    action: PayloadAction<IAvatarGiftAccess | null>
  ) => {
    state.giftAccess.data = action.payload
  },
  setGiftAccessLoading: (
    state: IAiAvatarState,
    action: PayloadAction<boolean>
  ) => {
    state.giftAccess.isLoading = action.payload
  },
  setIsGenerating: (state: IAiAvatarState, action: PayloadAction<boolean>) => {
    state.isGenerating = action.payload
  },
  setAwaitingGiftRecheck: (
    state: IAiAvatarState,
    action: PayloadAction<boolean>
  ) => {
    state.isAwaitingGiftRecheck = action.payload
  },
  // Replacing or resetting the form must also drop the voice-clone state: it
  // lives outside `generationParams`, and a stale clone left attached with the
  // Clone sub-tab active would silently override the loaded/reset `speakerId`
  // via `activeRefAudio` on the next trial preview or Generate. (The form-load
  // thunks then re-attach a clone-generated project's own clone on top of this
  // reset — see `restoreClonedVoice`.)
  setGenerationParams: (
    state: IAiAvatarState,
    action: PayloadAction<IAvatarGenerationParams>
  ) => {
    state.generationParams = action.payload
    state.voiceClone = voiceCloneInitial
  },
  resetGenerationParams: (state: IAiAvatarState) => {
    state.generationParams = avatarGenerationInitialParams
    state.voiceClone = voiceCloneInitial
  }
}

export default reducers
